import * as Sentry from "@sentry/react";
import type { ObservableMap } from "mobx";
import { makeAutoObservable, observable, reaction } from "mobx";
import type {
	ClearedFlightLevelMessage,
	ExitFlightLevelMessage,
	FlightConflictUpdateMessage,
	FlightEnteringAirspaceMessage,
	FlightMilestonePositionMessage,
	FlightRouteMessage,
	NewAircraftMessage,
	NewAircraftTypeMessage,
	NewFlightMessage,
	PositionAtObject,
	TargetReportMessage,
} from "../proto/ProtobufAirTrafficSimulator";
import { PlanningStage } from "../proto/ProtobufAirTrafficSimulator";
import type { FrontendPredictiveTrajectoryStateMessage } from "../schemas/mqttSubscriberSchemas";
import type {
	NormalizedGoal,
	PilotRequestJson,
} from "../schemas/pilotRequestSchema";
import {
	getPilotRequestType,
	normalizeGoal,
} from "../schemas/pilotRequestSchema";
import AircraftInfo from "./AircraftInfo";
import AircraftModel from "./AircraftModel";
import AircraftType from "./AircraftType";
import type BrainStore from "./BrainStore";
import CoordinatePair from "./CoordinatePair";
import convertTimestamp from "./convertTimestamp";
import type DatablockStore from "./DatablockStore";
import { createPairKey } from "./DatablockStore";
import FlightRoute from "./FlightRoute";
import type SectorStore from "./SectorStore";
import type SimulatorStore from "./SimulatorStore";
import Trajectory from "./Trajectory";

export default class AircraftStore {
	aircrafts: ObservableMap<string, AircraftModel> = observable.map(undefined, {
		deep: false,
	});

	aircraftInfo: ObservableMap<string, AircraftInfo> = observable.map(
		undefined,
		{ deep: false },
	);

	aircraftTypes: ObservableMap<string, AircraftType> = observable.map(
		undefined,
		{ deep: false },
	);

	flightRoutes: ObservableMap<string, FlightRoute> = observable.map(undefined, {
		deep: false,
	});

	hiddenFlights: Set<string> = observable.set(undefined, { deep: false });

	simulatorStore: SimulatorStore;

	sectorStore: SectorStore;

	brainStore: BrainStore;

	stcaConflictIds: ObservableMap<string, FlightConflictUpdateMessage> =
		observable.map(undefined, {
			deep: false,
		});

	stcaConflictFlightCounts: ObservableMap<string, number> = observable.map(
		undefined,
		{ deep: false },
	);

	mtcdConflictIds: ObservableMap<string, FlightConflictUpdateMessage> =
		observable.map(undefined, {
			deep: false,
		});

	tctConflictIds: ObservableMap<string, FlightConflictUpdateMessage> =
		observable.map(undefined, {
			deep: false,
		});

	tctConflictFlightCounts: ObservableMap<string, number> = observable.map(
		undefined,
		{ deep: false },
	);

	mtcdConflictFlightCounts: ObservableMap<string, number> = observable.map(
		undefined,
		{ deep: false },
	);

	/**
	 * Team assistant requests stored with flightId as key.
	 * Uses snake_case JSON format from IIS.
	 * Each aircraft can have multiple requests (stored as an array).
	 */
	teamAssistantRequests: ObservableMap<string, TeamAssistantRequest[]> =
		observable.map(undefined, {
			deep: false,
		});

	/**
	 * Tombstones for request IDs explicitly closed by the user/workflow.
	 * Any later stale update with the same requestId is ignored and cleared on MQTT.
	 */
	private closedTeamAssistantRequestIds = new Set<string>();

	/** Optional reference to DatablockStore for MTCD override logic */
	private datablockStore: DatablockStore | null = null;

	/**
	 * Buffer latest flight-level values by flight ID when XFL/CFL arrives
	 * before the corresponding NewFlight mapping is available.
	 */
	private pendingFlightLevelUpdates = new Map<
		string,
		{
			exitFlightLevel?: string;
			clearedFlightLevel?: string;
		}
	>();

	constructor({
		simulatorStore,
		sectorStore,
		brainStore,
	}: {
		simulatorStore: SimulatorStore;
		sectorStore: SectorStore;
		brainStore: BrainStore;
	}) {
		makeAutoObservable(
			this,
			{
				simulatorStore: false,
				sectorStore: false,
				brainStore: false,
			},
			{ autoBind: true },
		);
		this.simulatorStore = simulatorStore;
		this.sectorStore = sectorStore;
		this.brainStore = brainStore;

		// Reassign CPDLC capability whenever aircraft are added/removed,
		// based on alphabetical callSign ordering (first half gets CPDLC).
		reaction(
			() => this.aircrafts.size,
			() => this.reassignCPDLC(),
		);
	}

	/**
	 * Set the DatablockStore reference for MTCD override logic.
	 * Called after both stores are initialized to avoid circular dependency.
	 */
	setDatablockStore(datablockStore: DatablockStore): void {
		this.datablockStore = datablockStore;
	}

	get notHiddenAircrafts(): AircraftModel[] {
		return [...this.aircrafts.values()].filter(
			({ aircraftId }) => !this.hiddenFlights.has(aircraftId),
		);
	}

	get aircraftsWithRecentTargetReport(): AircraftModel[] {
		const timestamp = this.simulatorStore.minuteRoundedTimestamp;
		return this.notHiddenAircrafts.filter(
			({ lastTargetReportTime }) =>
				// Remove aircrafts with target reports older than 10 minutes
				// But keep the ones with no target report time because reasons
				timestamp - lastTargetReportTime < 600 || lastTargetReportTime === 0,
		);
	}

	get aircraftsWithPosition(): AircraftModel[] {
		return this.aircraftsWithRecentTargetReport.filter(
			({ lastKnownLongitude }) => lastKnownLongitude !== 0,
		);
	}

	handleNewFlight(newFlight: NewFlightMessage): void {
		const id = newFlight.aircraftId;
		let aircraft = this.aircrafts.get(id);
		if (aircraft) {
			aircraft.handleNewFlightUpdate(newFlight);
		} else {
			aircraft = new AircraftModel({
				aircraftId: id,
				assignedFlightId: newFlight.flightUniqueId,
				callSign: newFlight.callSign,
				airlineIcaoCode: newFlight.airlineIcaoCode,
				airlineCallSign: newFlight.airlineCallSign,
				arrivalAirport: newFlight.arrivalAirport,
				departureAirport: newFlight.departureAirport,
				aircraftInfo: this.aircraftInfo,
				aircraftTypes: this.aircraftTypes,
				flightRoutes: this.flightRoutes,
				sectorStore: this.sectorStore,
				simulatorStore: this.simulatorStore,
			});
			this.aircrafts.set(id, aircraft);
		}

		this.applyPendingFlightLevelUpdates(newFlight.flightUniqueId, aircraft);
	}

	handleTargetReport(targetReport: TargetReportMessage): void {
		const { vehicleId } = targetReport;
		const aircraft = this.aircrafts.get(vehicleId);
		if (!aircraft) {
			return;
		}
		aircraft.handleTargetReport(targetReport);
	}

	handleNewAircraftMessage(newAircraftMessage: NewAircraftMessage): void {
		const { aircraftId, wakeTurbulenceCategory, aircraftType } =
			newAircraftMessage;
		const aircraftInfo = this.aircraftInfo.get(aircraftId);
		if (aircraftInfo) {
			aircraftInfo.setWakeTurbulenceCategory(wakeTurbulenceCategory);
		} else {
			this.aircraftInfo.set(
				aircraftId,
				new AircraftInfo({
					aircraftId,
					aircraftType,
					wakeTurbulenceCategory,
				}),
			);
		}
	}

	handleNewFlightRoute(route: FlightRouteMessage): void {
		const id = route.flightUniqueId;

		// It seems that the trajectory list is ordered, and each time a positionAtObject is sent,
		// It is immediately followed by a position4D. We merge them together for simplicity
		let previousPositionObject: PositionAtObject | undefined;

		const trajectories =
			route.route?.trajectory
				?.map((area) => {
					let position4d;
					if (area.position.oneofKind === "position4D") {
						position4d = area.position.position4D;
					} else if (area.position.oneofKind === "positionAtObject") {
						previousPositionObject = area.position.positionAtObject;
						return;
					} else {
						throw new Error("No position4d or positionatobject");
					}

					const { time, latitude, longitude, altitude } = position4d;
					if (!time) {
						throw new Error("Missing time in flight route message position4d");
					}
					const timestamp = convertTimestamp(time);

					const objectId = previousPositionObject?.objectId;

					const trajectory = new Trajectory({
						trajectoryCoordinate: new CoordinatePair({
							latitude,
							longitude,
							altitude,
						}),
						timestamp,
						objectId,
					});
					previousPositionObject = undefined;
					return trajectory;
				})
				?.filter(
					(trajectory): trajectory is Trajectory => trajectory !== undefined,
				) ?? [];

		this.flightRoutes.set(
			id,
			new FlightRoute({
				flightId: id,
				trajectory: trajectories,
			}),
		);
	}

	handleFlightNewMilestonePositions(
		milestone: FlightMilestonePositionMessage,
	): void {
		const { planningStage, flightUniqueId } = milestone;

		// find the aircraft in this.aircrafts map with
		// an assignedFlightId matching the flightUniqueId
		let aircraft;
		for (const potentialAircraft of this.aircrafts.values()) {
			if (potentialAircraft.assignedFlightId === flightUniqueId) {
				aircraft = potentialAircraft;
				break;
			}
		}

		if (!aircraft) {
			// biome-ignore lint/suspicious/noConsole: needed for now
			console.warn(
				"Received milestone position for unknown aircraft",
				flightUniqueId,
			);
			Sentry.captureMessage(
				`Received milestone position for unknown aircraft: ${flightUniqueId}`,
				"warning",
			);
			return;
		}

		if (
			planningStage === PlanningStage.TARGET ||
			planningStage === PlanningStage.PLAN
		) {
			aircraft.handleTargetMilestone(milestone);
		}

		if (planningStage === PlanningStage.ACTUAL) {
			aircraft.handleActualMilestone(milestone);
		}

		if (
			planningStage !== PlanningStage.TARGET &&
			planningStage !== PlanningStage.PLAN &&
			planningStage !== PlanningStage.ACTUAL
		) {
			// biome-ignore lint/suspicious/noConsole: temporary debugging for unexpected planning stage values
			console.warn("Ignoring milestone with unsupported planning stage", {
				flightUniqueId,
				planningStage,
				milestone: milestone.milestone,
				objectId: milestone.position?.objectId,
			});
		}
	}

	handleNewSectorInFlightMessage(
		flightEnteringAirspaceMessage: FlightEnteringAirspaceMessage,
	): void {
		const { flightUniqueId } = flightEnteringAirspaceMessage;
		const aircraft = this.aircrafts.get(flightUniqueId);
		if (!aircraft) {
			// biome-ignore lint/suspicious/noConsole: needed for now
			console.warn(
				"Received sector in flight message for unknown aircraft",
				flightUniqueId,
			);
			Sentry.captureMessage(
				`Received sector in flight message for unknown aircraft: ${flightUniqueId}`,
				"warning",
			);
			return;
		}
		aircraft.handleSectorInFlightMessage(flightEnteringAirspaceMessage);
	}

	handleExitFlightLevelMessage(message: ExitFlightLevelMessage): void {
		const { flightId, exitFlightLevel } = message;
		const aircraft = this.getAircraftByFlightId(flightId);

		if (!aircraft) {
			this.queuePendingFlightLevelUpdate(flightId, {
				exitFlightLevel: exitFlightLevel.toString(),
			});
			return;
		}

		aircraft.setNextSectorFL(exitFlightLevel.toString());
	}

	handleClearedFlightLevelMessage(message: ClearedFlightLevelMessage): void {
		const { flightId, clearedFlightLevel } = message;
		const aircraft = this.getAircraftByFlightId(flightId);

		if (!aircraft) {
			this.queuePendingFlightLevelUpdate(flightId, {
				clearedFlightLevel: clearedFlightLevel.toString(),
			});
			return;
		}

		aircraft.applyClearedFlightLevel(clearedFlightLevel.toString());
	}

	private getAircraftByFlightId(flightId: string): AircraftModel | undefined {
		const directMatch = this.aircrafts.get(flightId);
		if (directMatch) {
			return directMatch;
		}

		for (const aircraft of this.aircrafts.values()) {
			if (aircraft.assignedFlightId === flightId) {
				return aircraft;
			}
		}

		return undefined;
	}

	private queuePendingFlightLevelUpdate(
		flightId: string,
		update: { exitFlightLevel?: string; clearedFlightLevel?: string },
	): void {
		const existing = this.pendingFlightLevelUpdates.get(flightId);
		this.pendingFlightLevelUpdates.set(flightId, {
			exitFlightLevel: update.exitFlightLevel ?? existing?.exitFlightLevel,
			clearedFlightLevel:
				update.clearedFlightLevel ?? existing?.clearedFlightLevel,
		});
	}

	private applyPendingFlightLevelUpdates(
		flightId: string,
		aircraft: AircraftModel,
	): void {
		const pending = this.pendingFlightLevelUpdates.get(flightId);
		if (!pending) {
			return;
		}

		if (pending.exitFlightLevel !== undefined) {
			aircraft.setNextSectorFL(pending.exitFlightLevel);
		}
		if (pending.clearedFlightLevel !== undefined) {
			aircraft.applyClearedFlightLevel(pending.clearedFlightLevel);
		}

		this.pendingFlightLevelUpdates.delete(flightId);
	}

	handleNewAircraftTypeMessage(
		newAircraftTypeMessage: NewAircraftTypeMessage,
	): void {
		const aircraftType = AircraftType.fromProto(newAircraftTypeMessage);
		this.aircraftTypes.set(aircraftType.vehicleTypeId, aircraftType);
	}

	handleFrontendFlightController(flightId: string, controller: string): void {
		this.aircrafts.get(flightId)?.setController(controller);
	}

	handleFrontendACCFlightLevel(flightId: string, flightLevel: string): void {
		this.aircrafts.get(flightId)?.setNextACCFL(flightLevel, true); // From MQTT = manually set
	}

	handleFrontendNextSectorFlightLevel(
		flightId: string,
		flightLevel: string,
	): void {
		this.aircrafts.get(flightId)?.setNextSectorFL(flightLevel);
	}

	handleFrontendACCBearing(flightId: string, bearing: number): void {
		this.aircrafts.get(flightId)?.setAssignedBearing(bearing);
	}

	handleFrontendAssignedFlightLevel(
		flightId: string,
		flightLevel: string,
	): void {
		this.aircrafts.get(flightId)?.setAssignedFlightLevel(flightLevel);
	}

	handleFrontendSpeed(flightId: string, speed: number): void {
		this.aircrafts.get(flightId)?.setAssignedSpeed(speed);
	}

	handleFrontendPredictiveTrajectoryState(
		flightId: string,
		state: FrontendPredictiveTrajectoryStateMessage | null,
	): void {
		const aircraft =
			this.getAircraftByFlightId(flightId) ?? this.aircrafts.get(flightId);
		if (!aircraft) {
			return;
		}

		if (!state || state.mode === "unset") {
			aircraft.clearPredictiveTrajectoryState();
			return;
		}

		if (state.mode === "rerouted") {
			aircraft.setPredictiveTrajectoryRerouted();
			return;
		}

		aircraft.setPredictiveTrajectoryReroutedViaWaypoint(
			state.waypointId,
			state.latitude,
			state.longitude,
		);
	}

	handleFrontendLocalAssignedFlightLevel(
		flightId: string,
		flightLevel: string,
	): void {
		this.aircrafts.get(flightId)?.setLocalAssignedFlightLevel(flightLevel);
	}

	handleFrontendFlightHidden(aircraftId: string, hidden: boolean): void {
		if (hidden) {
			this.hiddenFlights.add(aircraftId);
		} else {
			this.hiddenFlights.delete(aircraftId);
		}
	}

	handleNewConflictMessage(
		flightConflictUpdate: FlightConflictUpdateMessage,
	): void {
		const flight1 = flightConflictUpdate.flightId;
		const flight2 = flightConflictUpdate.conflictingFlightId;
		if (!flight1 || !flight2) {
			return;
		}
		switch (flightConflictUpdate.conflictUpdate) {
			case 0: // New Conflict
			case 1: // Update conflict
				switch (flightConflictUpdate.conflictType) {
					case 0: // STCA Conflict
						this.upsertConflictMessage(
							this.stcaConflictIds,
							this.stcaConflictFlightCounts,
							flightConflictUpdate,
						);
						break;
					case 1: // TCT Conflict
						this.upsertConflictMessage(
							this.tctConflictIds,
							this.tctConflictFlightCounts,
							flightConflictUpdate,
						);
						break;
					case 3: // MTCD Conflict (Severe)
					case 4: // MTCD Conflict (Potential)
						this.upsertConflictMessage(
							this.mtcdConflictIds,
							this.mtcdConflictFlightCounts,
							flightConflictUpdate,
						);
						// Remove any custom datablock for this pair (MTCD takes priority)
						if (this.datablockStore) {
							const pairKey = createPairKey(
								flightConflictUpdate.flightId,
								flightConflictUpdate.conflictingFlightId,
							);
							this.datablockStore.removeByPairKey(pairKey);
						}
						break;
					default:
						// Handle unexpected conflictType values
						break;
				}
				break;

			case 2: // Conflict cleared
				switch (flightConflictUpdate.conflictType) {
					case 0: // STCA Conflict cleared
						this.clearConflictMessage(
							this.stcaConflictIds,
							this.stcaConflictFlightCounts,
							flightConflictUpdate.id.toString(),
						);
						break;
					case 1: // TCT Conflict
						this.clearConflictMessage(
							this.tctConflictIds,
							this.tctConflictFlightCounts,
							flightConflictUpdate.id.toString(),
						);
						break;
					case 3: // MTCD Conflict (MTCDInputSevere)
					case 4: // MTCD Conflict (MTCDInputPotential)
						this.clearConflictMessage(
							this.mtcdConflictIds,
							this.mtcdConflictFlightCounts,
							flightConflictUpdate.id.toString(),
						);
						break;
					default:
						// Handle unexpected conflictType values
						break;
				}
				break;
			default:
				// Handle unexpected conflictUpdate values
				break;
		}
	}

	private adjustFlightConflictCount(
		flightCounts: ObservableMap<string, number>,
		flightId: string | undefined,
		delta: 1 | -1,
	): void {
		if (!flightId) {
			return;
		}

		const previous = flightCounts.get(flightId) ?? 0;
		const next = previous + delta;

		if (next <= 0) {
			flightCounts.delete(flightId);
			return;
		}

		flightCounts.set(flightId, next);
	}

	private addConflictMessageToCounts(
		flightCounts: ObservableMap<string, number>,
		conflictMessage: FlightConflictUpdateMessage,
	): void {
		this.adjustFlightConflictCount(flightCounts, conflictMessage.flightId, 1);
		this.adjustFlightConflictCount(
			flightCounts,
			conflictMessage.conflictingFlightId,
			1,
		);
	}

	private removeConflictMessageFromCounts(
		flightCounts: ObservableMap<string, number>,
		conflictMessage: FlightConflictUpdateMessage,
	): void {
		this.adjustFlightConflictCount(flightCounts, conflictMessage.flightId, -1);
		this.adjustFlightConflictCount(
			flightCounts,
			conflictMessage.conflictingFlightId,
			-1,
		);
	}

	private upsertConflictMessage(
		conflicts: ObservableMap<string, FlightConflictUpdateMessage>,
		flightCounts: ObservableMap<string, number>,
		conflictMessage: FlightConflictUpdateMessage,
	): void {
		const conflictId = conflictMessage.id.toString();
		const previous = conflicts.get(conflictId);

		if (previous) {
			this.removeConflictMessageFromCounts(flightCounts, previous);
		}

		conflicts.set(conflictId, conflictMessage);
		this.addConflictMessageToCounts(flightCounts, conflictMessage);
	}

	private clearConflictMessage(
		conflicts: ObservableMap<string, FlightConflictUpdateMessage>,
		flightCounts: ObservableMap<string, number>,
		conflictId: string,
	): void {
		const existing = conflicts.get(conflictId);
		if (!existing) {
			return;
		}

		this.removeConflictMessageFromCounts(flightCounts, existing);
		conflicts.delete(conflictId);
	}

	hasStcaConflict(flightId: string): boolean {
		return (this.stcaConflictFlightCounts.get(flightId) ?? 0) > 0;
	}
	hasTctConflict(flightId: string): boolean {
		return (this.tctConflictFlightCounts.get(flightId) ?? 0) > 0;
	}

	getConflictPairAircraftIdsForFlightId(
		flightId: string,
		conflictKind: "stca" | "tct",
	): [string, string] | null {
		const conflicts =
			conflictKind === "stca" ? this.stcaConflictIds : this.tctConflictIds;

		for (const conflictMessage of conflicts.values()) {
			if (
				conflictMessage.flightId !== flightId &&
				conflictMessage.conflictingFlightId !== flightId
			) {
				continue;
			}

			const aircraftId1 = this.resolveAircraftIdForConflictFlightId(
				conflictMessage.flightId,
			);
			const aircraftId2 = this.resolveAircraftIdForConflictFlightId(
				conflictMessage.conflictingFlightId,
			);

			if (aircraftId1 && aircraftId2) {
				return [aircraftId1, aircraftId2];
			}
		}

		return null;
	}

	private resolveAircraftIdForConflictFlightId(
		flightId: string,
	): string | null {
		if (this.aircrafts.has(flightId)) {
			return flightId;
		}

		for (const aircraft of this.aircrafts.values()) {
			if (aircraft.assignedFlightId === flightId) {
				return aircraft.aircraftId;
			}
		}

		return null;
	}
	hasMtcdConflict(flightId: string): boolean {
		return (this.mtcdConflictFlightCounts.get(flightId) ?? 0) > 0;
	}

	getMtcdConflictFlightIds(): string[] {
		const ids = new Set<string>();
		for (const msg of this.mtcdConflictIds.values()) {
			if (msg.flightId) {
				ids.add(msg.flightId);
			}
			if (msg.conflictingFlightId) {
				ids.add(msg.conflictingFlightId);
			}
		}
		return [...ids];
	}

	/**
	 * Check if a pair of aircraft has an MTCD conflict.
	 */
	hasMtcdConflictForPair(id1: string, id2: string): boolean {
		for (const msg of this.mtcdConflictIds.values()) {
			if (
				(msg.flightId === id1 && msg.conflictingFlightId === id2) ||
				(msg.flightId === id2 && msg.conflictingFlightId === id1)
			) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Find the nearest aircraft to a screen position within a pixel threshold
	 * @param mouseScreenX Screen X coordinate in pixels
	 * @param mouseScreenY Screen Y coordinate in pixels
	 * @param maxDistancePixels Maximum distance in pixels to consider
	 * @param mapViewportStore MapViewportStore for coordinate projection
	 * @param excludeAircraftId Optional aircraft ID to exclude from search
	 * @returns Nearest aircraft info or null if none found within threshold
	 */
	findNearestAircraftInScreenSpace(
		mouseScreenX: number,
		mouseScreenY: number,
		maxDistancePixels: number,
		mapViewportStore: {
			projectPosition: (
				lat: number,
				lng: number,
			) => { x: number; y: number } | null;
		},
		excludeAircraftId?: string | null,
	): { aircraftId: string; position: [number, number] } | null {
		let nearestAircraft: {
			aircraftId: string;
			position: [number, number];
		} | null = null;
		let minDistance = maxDistancePixels;

		for (const aircraft of this.aircraftsWithPosition) {
			// Skip the excluded aircraft (typically the source aircraft)
			if (excludeAircraftId && aircraft.aircraftId === excludeAircraftId) {
				continue;
			}

			// Project aircraft position to screen coordinates
			const screenPos = mapViewportStore.projectPosition(
				aircraft.lastKnownLatitude,
				aircraft.lastKnownLongitude,
			);

			// Skip if projection failed (aircraft off-screen)
			if (!screenPos) {
				continue;
			}

			// Calculate pixel distance using Euclidean distance
			const dx = screenPos.x - mouseScreenX;
			const dy = screenPos.y - mouseScreenY;
			const distance = Math.hypot(dx, dy);

			// Track closest aircraft within threshold
			if (distance < minDistance) {
				minDistance = distance;
				nearestAircraft = {
					aircraftId: aircraft.aircraftId,
					position: [aircraft.lastKnownLongitude, aircraft.lastKnownLatitude],
				};
			}
		}

		return nearestAircraft;
	}

	// =========================================================================
	// Team Assistant Request Methods
	// =========================================================================

	/**
	 * Get all requests for an aircraft.
	 * Returns an array of requests, sorted by timestamp (newest first).
	 */
	getRequestsForAircraft(flightId: string): TeamAssistantRequest[] {
		const requests = this.teamAssistantRequests.get(flightId) ?? [];
		// Sort by timestamp (newest first)
		return requests.toSorted((a, b) => {
			const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
			const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
			return timeB - timeA;
		});
	}

	/**
	 * Get the first (most recent) request for an aircraft.
	 */
	getFirstRequestForAircraft(
		flightId: string,
	): TeamAssistantRequest | undefined {
		return this.getRequestsForAircraft(flightId)[0];
	}

	/**
	 * Add a team assistant request from JSON (snake_case format).
	 * Key is the flightId - each aircraft can have multiple requests.
	 * If a request with the same requestId exists, it will be updated.
	 * Calculates autonomyProfile once when request arrives (immutable).
	 */
	setTeamAssistantRequest(
		flightId: string,
		requestId: string,
		request: PilotRequestJson,
	): void {
		if (this.closedTeamAssistantRequestIds.has(requestId)) {
			// Request was explicitly closed earlier. Ignore stale replay.
			return;
		}

		// Calculate autonomyProfile once based on request type (immutable)
		const rawRequestType = request.context?.request_type ?? 0;
		const requestType = getPilotRequestType(rawRequestType);
		const currentAutonomyProfile =
			this.brainStore.getAPForRequestType(requestType);
		const normalizedGoals = request.goals.map((goal) =>
			normalizeGoal(goal, requestType),
		);

		const existingRequests = this.teamAssistantRequests.get(flightId) ?? [];
		// Check if request with same ID exists and update it, otherwise add
		const existingIndex = existingRequests.findIndex(
			(r) => r.requestId === requestId,
		);

		const existingRequest =
			existingIndex === -1 ? undefined : existingRequests[existingIndex];
		const autonomyProfile =
			existingRequest?.autonomyProfile ?? currentAutonomyProfile;

		const teamAssistantRequest: TeamAssistantRequest = {
			...request,
			flightId,
			requestId,
			autonomyProfile,
			normalizedGoals,
		};

		if (existingIndex === -1) {
			this.teamAssistantRequests.set(flightId, [
				...existingRequests,
				teamAssistantRequest,
			]);

			// Track arrival for "last 5 min" count (survives accept/dismiss)
			this.brainStore.recordRequestArrival();
		} else {
			existingRequests[existingIndex] = teamAssistantRequest;
			this.teamAssistantRequests.set(flightId, [...existingRequests]);
		}
	}

	/**
	 * Remove a specific team assistant request by requestId.
	 * If no requestId provided, removes all requests for the aircraft.
	 */
	removeTeamAssistantRequest(flightId: string, requestId?: string): void {
		if (!requestId) {
			const existingRequests = this.teamAssistantRequests.get(flightId) ?? [];
			for (const req of existingRequests) {
				this.closedTeamAssistantRequestIds.add(req.requestId);
			}
			this.teamAssistantRequests.delete(flightId);
			return;
		}

		this.closedTeamAssistantRequestIds.add(requestId);

		const existingRequests = this.teamAssistantRequests.get(flightId) ?? [];
		const filtered = existingRequests.filter((r) => r.requestId !== requestId);

		if (filtered.length === 0) {
			this.teamAssistantRequests.delete(flightId);
		} else {
			this.teamAssistantRequests.set(flightId, filtered);
		}
	}

	/**
	 * Remove a specific team assistant request by requestId, regardless of flight.
	 * Returns true if a matching request was found and removed.
	 */
	removeTeamAssistantRequestByRequestId(requestId: string): boolean {
		for (const [flightId, requests] of this.teamAssistantRequests.entries()) {
			if (requests.some((request) => request.requestId === requestId)) {
				this.removeTeamAssistantRequest(flightId, requestId);
				return true;
			}
		}

		return false;
	}

	/**
	 * Check if an aircraft has any team assistant requests.
	 */
	hasTeamAssistantRequests(flightId: string): boolean {
		const requests = this.teamAssistantRequests.get(flightId);
		return requests !== undefined && requests.length > 0;
	}

	/**
	 * Deterministically assign CPDLC capability: sort all aircraft alphabetically
	 * by callSign, first half gets hasCPDLC = true, second half gets false.
	 */
	private reassignCPDLC(): void {
		const sorted = [...this.aircrafts.values()].toSorted((a, b) =>
			a.callSign.localeCompare(b.callSign),
		);
		const halfIndex = Math.ceil(sorted.length / 2);
		for (const [index, aircraft] of sorted.entries()) {
			aircraft.hasCPDLC = index < halfIndex;
		}
	}
}

/**
 * Team Assistant Request type using snake_case JSON format (from IIS).
 * Extends PilotRequestJson with flightId, requestId, and autonomyProfile.
 * autonomyProfile is calculated once when the request arrives and never changes.
 */
export interface TeamAssistantRequest extends PilotRequestJson {
	flightId: string;
	requestId: string;
	autonomyProfile: number; // Calculated once on arrival (1 or 2), defaults to 1 if data missing
	normalizedGoals: NormalizedGoal[];
}
